// src/pages/OrdersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { logEmployeeActivity } from "../utils/activityLogger";

import "./Orders.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";

/* ---------------- Helpers (same as before) ---------------- */
const tryParseExcelSerial = (val) => {
  try {
    if (typeof val === "number") {
      const epoch = new Date(Date.UTC(1899, 11, 30));
      const days = Math.floor(val);
      const ms = (val - days) * 24 * 60 * 60 * 1000;
      const date = new Date(epoch.getTime() + days * 24 * 60 * 60 * 1000 + ms);
      return date.toISOString().slice(0, 10);
    }
  } catch (e) {}
  return "";
};

const normalizeRow = (raw) => {
  const get = (candidates) => {
    for (const c of candidates) {
      if (raw[c] !== undefined && raw[c] !== null) return raw[c];
      const match = Object.keys(raw).find(k => k.trim().toLowerCase() === c.trim().toLowerCase());
      if (match) return raw[match];
    }
    return null;
  };
  const id = get(["id", "order_id", "order id", "orderid"]) || "";
  const customer = get(["customer", "name", "buyer", "customer name"]) || "";
  const product = get(["product", "product name", "title"]) || "";
  const category = get(["category", "cat", "product category"]) || "";
  const rawAmount = get(["amount", "price", "order_value", "total"]) || 0;
  const amount = Number(String(rawAmount).replace(/[^0-9\.\-]/g, "")) || 0;
  const status = get(["status", "order_status"]) || "Pending";
  const dateVal = get(["date", "order_date", "created_at"]) || "";
  let date = "";
  if (typeof dateVal === "number") date = tryParseExcelSerial(dateVal);
  else if (dateVal) {
    try {
      const d = new Date(dateVal);
      if (!isNaN(d)) date = d.toISOString().slice(0, 10);
    } catch {}
  }
  const notes = get(["notes", "remark", "remarks"]) || "";
  return {
    id,
    customer: String(customer).trim(),
    product: String(product).trim(),
    category: String(category).trim(),
    amount,
    status: String(status).trim(),
    date,
    notes: String(notes).trim(),
    _skip: false,
  };
};

/* ---------------- Small UI ---------------- */
const OrderScanner = ({ onScan }) => (
  <button className="btn scanner-btn" onClick={onScan}>📷 Scan Order</button>
);

const UploadArea = ({ onParsed }) => {
  const fileRef = React.useRef(null);
  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const rows = json.map(r => normalizeRow(r));
        onParsed(rows, file.name);
      } catch (err) {
        console.error("Failed to parse upload:", err);
        alert("Failed to parse file. Make sure it's a valid Excel/CSV.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = null;
        }}
      />
      <button className="btn outline" onClick={() => fileRef.current && fileRef.current.click()}>
        📁 Upload Excel/CSV
      </button>
      <div style={{ fontSize: 13, color: "#64748b" }}>Supports .xlsx .xls .csv — first sheet used</div>
    </div>
  );
};

/* ---------------- FiltersBar (with Date View toggle) ---------------- */
function FiltersBar({
  q, setQ,
  fromDate, setFromDate,
  toDate, setToDate,
  filterCategory, setFilterCategory,
  filterStatus, setFilterStatus,
  minAmount, setMinAmount,
  maxAmount, setMaxAmount,
  categories,
  onReset, onExport, onRefresh, onClearUpload,
  showByDate, setShowByDate
}) {
  return (
    <div className="card" style={{ marginTop: 12, padding: 12 }}>
      <div style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        width: "100%",
        flexWrap: "wrap"
      }}>
        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 320px" }}>
          <input
            placeholder="Search id / customer / product..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ padding: 10, borderRadius: 8, border: "1px solid #e6e9f2", width: "100%" }}
          />
        </div>

        {/* Filters */}
        <div style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flex: "1 1 520px",
          justifyContent: "center",
          flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label className="kv">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label className="kv">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label className="kv">Category</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label className="kv">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All</option>
              <option>Pending</option>
              <option>Accepted</option>
              <option>Rejected</option>
              <option>Cancelled</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label className="kv">Amount</label>
            <input type="number" placeholder="Min" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} style={{ width: 100 }} />
            <input type="number" placeholder="Max" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} style={{ width: 100 }} />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
          <button className="btn outline" onClick={onReset}>Reset</button>
          <button className="btn outline" onClick={onClearUpload}>Clear Upload</button>
          <button className="btn outline" onClick={onRefresh}>Refresh</button>
          <button className={`btn ${showByDate ? "primary" : ""}`} onClick={() => setShowByDate(!showByDate)}>
            {showByDate ? "Date View: ON" : "Date View: OFF"}
          </button>
          <button className="btn" onClick={onExport}>Export CSV</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Table (unchanged) ---------------- */
const OrderTable = ({ orders = [], onView, onEdit, onDelete }) => {
  return (
    <table className="order-table">
      <thead>
        <tr>
          <th>SL</th>
          <th>Order ID</th>
          <th>Date</th>
          <th>Customer</th>
          <th>Product</th>
          <th>Category</th>
          <th>Amount</th>
          <th>Status</th>
          <th className="actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {orders.length === 0 ? (
          <tr className="empty">
            <td colSpan="9" align="center">No Orders Found</td>
          </tr>
        ) : (
          orders.map((order, idx) => (
            <tr key={order._uid ?? order.id ?? idx}>
              <td>{idx + 1}</td>
              <td>{order.id || "-"}</td>
              <td>{order.date || "-"}</td>
              <td>{order.customer}</td>
              <td>{order.product}</td>
              <td>{order.category}</td>
              <td>₹{Number(order.amount || 0).toLocaleString()}</td>
              <td>
                <span className={`status-badge ${String(order.status || "pending").toLowerCase()}`}>
                  {order.status || "Pending"}
                </span>
              </td>
              <td className="actions-col">
                <button className="btn outline" onClick={() => onView(order)}>👁 View</button>
                <button className="btn" onClick={() => onEdit(order)}>✏ Edit</button>
                <button className="btn danger" onClick={() => onDelete(order.id)}>🗑 Delete</button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

/* ------------- DateGroupedView (new) ------------- */
/*
  groups: { "2025-11-30": [order,...], "No Date": [...] }
  sorted by date desc
*/
function DateGroupedView({ orders = [], onView, onEdit, onDelete }) {
  const [openDates, setOpenDates] = useState(() => ({}));

  const groups = useMemo(() => {
    const m = {};
    for (const o of orders) {
      const d = o.date || "No Date";
      if (!m[d]) m[d] = [];
      m[d].push(o);
    }
    // sort orders inside date by amount desc or id
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (b.amount || 0) - (a.amount || 0));
    }
    return m;
  }, [orders]);

  const sortedDates = useMemo(() => {
    const keys = Object.keys(groups);
    // put "No Date" at end
    keys.sort((a, b) => {
      if (a === "No Date") return 1;
      if (b === "No Date") return -1;
      return new Date(b) - new Date(a);
    });
    return keys;
  }, [groups]);

  const toggle = (d) => setOpenDates(s => ({ ...s, [d]: !s[d] }));

  return (
    <div className="date-groups">
      {sortedDates.map(dateKey => {
        const list = groups[dateKey] || [];
        const totalAmt = list.reduce((s, x) => s + (Number(x.amount || 0)), 0);
        const isOpen = !!openDates[dateKey];
        return (
          <div className="date-group" key={dateKey}>
            <div className="date-header" onClick={() => toggle(dateKey)}>
              <div>
                <div className="date-title">{dateKey}</div>
                <div className="date-sub">{list.length} orders • ₹{(totalAmt || 0).toLocaleString()}</div>
              </div>
              <div className="date-chevron">{isOpen ? "▾" : "▸"}</div>
            </div>

            {isOpen && (
              <div className="date-orders">
                <table className="order-table small">
                  <thead>
                    <tr>
                      <th>SL</th>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((o, idx) => (
                      <tr key={o._uid ?? o.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td>{o.id || "-"}</td>
                        <td>{o.customer}</td>
                        <td>{o.product}</td>
                        <td>₹{Number(o.amount || 0).toLocaleString()}</td>
                        <td><span className={`status-badge ${String(o.status || "pending").toLowerCase()}`}>{o.status || "Pending"}</span></td>
                        <td className="actions-col">
                          <button className="btn outline" onClick={() => onView(o)}>👁 View</button>
                          <button className="btn" onClick={() => onEdit(o)}>✏ Edit</button>
                          <button className="btn danger" onClick={() => onDelete(o.id)}>🗑 Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      {sortedDates.length === 0 && <div style={{ padding: 16 }}>No orders to show</div>}
    </div>
  );
}

/* ---------------- Main OrdersPage (wire-up) ---------------- */
export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);

  // upload preview
  const [uploadRows, setUploadRows] = useState(null);
  const [uploadFilename, setUploadFilename] = useState("");
  const [importDate, setImportDate] = useState(new Date().toISOString().slice(0, 10));
  const [importing, setImporting] = useState(false);

  // filters/search
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // NEW: show by date toggle
  const [showByDate, setShowByDate] = useState(false);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/orders`);
      const data = response.data || [];
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadOrders error:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrders(); }, []);

  const onParsed = (rows, filename) => {
    const withUid = rows.map((r, i) => ({ ...r, _uid: `u-${Date.now()}-${i}` }));
    setUploadRows(withUid);
    setUploadFilename(filename || "upload");
  };

  const toggleSkip = (uid) => setUploadRows(rs => rs.map(r => r._uid === uid ? { ...r, _skip: !r._skip } : r));
  const updateUploadRow = (uid, key, value) => setUploadRows(rs => rs.map(r => r._uid === uid ? { ...r, [key]: value } : r));

  const handleImport = async () => {
    if (!uploadRows || uploadRows.length === 0) return alert("No rows to import.");
    const toImport = uploadRows.filter(r => !r._skip).map(r => ({ ...r, date: r.date || importDate }));
    if (toImport.length === 0) return alert("All rows are skipped.");
    if (!confirm(`Import ${toImport.length} orders?`)) return;

    setImporting(true);
    try {
      await axios.post(`${API_BASE}/orders/import`, { orders: toImport });
      void logEmployeeActivity({
        action: "Orders Imported",
        module: "Orders",
        description: `Imported ${toImport.length} orders from ${uploadFilename || "uploaded file"}.`,
      });
      alert("Import successful");
      setUploadRows(null);
      setUploadFilename("");
      await loadOrders();
    } catch (err) {
      console.error("import failed", err);
      alert("Import failed: " + (err?.response?.data?.message || err?.message || "See console"));
    } finally {
      setImporting(false);
    }
  };

  const filtered = useMemo(() => {
    const ql = (q || "").trim().toLowerCase();
    return (orders || []).filter(o => {
      if (ql) {
        const inQ = (String(o.customer || "").toLowerCase().includes(ql) ||
          String(o.product || "").toLowerCase().includes(ql) ||
          String(o.id || "").toLowerCase().includes(ql));
        if (!inQ) return false;
      }
      if (fromDate) {
        if (!o.date) return false;
        if (new Date(o.date) < new Date(fromDate)) return false;
      }
      if (toDate) {
        if (!o.date) return false;
        if (new Date(o.date) > new Date(toDate)) return false;
      }
      if (filterCategory) {
        if ((String(o.category || "").toLowerCase()) !== filterCategory.toLowerCase()) return false;
      }
      const amt = Number(o.amount || 0);
      if (minAmount && amt < Number(minAmount)) return false;
      if (maxAmount && amt > Number(maxAmount)) return false;
      if (filterStatus && String(o.status || "").toLowerCase() !== filterStatus.toLowerCase()) return false;
      return true;
    });
  }, [orders, q, fromDate, toDate, filterCategory, minAmount, maxAmount, filterStatus]);

  const exportCSV = () => {
    const rows = filtered;
    if (!rows || rows.length === 0) return alert("No data to export.");
    const header = ["id", "date", "customer", "product", "category", "amount", "status", "notes"];
    const csv = [header.join(",")].concat(rows.map(r => header.map(h => {
      const val = r[h] == null ? "" : String(r[h]);
      return (val.includes(",") || val.includes('"')) ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `orders_export_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleView = (o) => setViewOrder(o);
  const closeView = () => setViewOrder(null);
  const handleEdit = (o) => setEditOrder(o);
  const handleUpdateOrder = async (updated) => {
    try {
      await axios.put(`${API_BASE}/orders/${updated.id}`, updated);
      void logEmployeeActivity({
        action: "Order Updated",
        module: "Orders",
        description: `Updated order ${updated.id || "-"} for ${updated.customer || "customer"}.`,
      });
      await loadOrders();
      setEditOrder(null);
    } catch (err) {
      console.error("updateOrder", err);
      alert("Update failed: " + (err?.response?.data?.message || err?.message));
    }
  };
  const handleDelete = async (id) => {
    if (!confirm("Delete this order?")) return;
    const orderToDelete = orders.find((order) => String(order.id) === String(id));
    try {
      await axios.delete(`${API_BASE}/orders/${id}`);
      void logEmployeeActivity({
        action: "Order Deleted",
        module: "Orders",
        description: `Deleted order ${orderToDelete?.id || id} for ${orderToDelete?.customer || "customer"}.`,
      });
      await loadOrders();
    } catch (err) {
      console.error("delete", err);
      alert("Delete failed: " + (err?.response?.data?.message || err?.message));
    }
  };

  const categories = useMemo(() => {
    const s = new Set();
    for (const o of orders) if (o.category) s.add(o.category);
    return Array.from(s).sort();
  }, [orders]);

  const handleResetFilters = () => {
    setQ(""); setFromDate(""); setToDate(""); setFilterCategory(""); setFilterStatus(""); setMinAmount(""); setMaxAmount("");
  };

  return (
    <div className="page-container orders-page">
      <div className="page-head orders-page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2>Orders</h2>
          <div className="hint">Upload Excel, preview & import orders. Filters & export available. Toggle Date View to see date-wise groups.</div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <OrderScanner onScan={() => loadOrders()} />
          <UploadArea onParsed={onParsed} />
        </div>
      </div>

      <FiltersBar
        q={q} setQ={setQ}
        fromDate={fromDate} setFromDate={setFromDate}
        toDate={toDate} setToDate={setToDate}
        filterCategory={filterCategory} setFilterCategory={setFilterCategory}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        minAmount={minAmount} setMinAmount={setMinAmount}
        maxAmount={maxAmount} setMaxAmount={setMaxAmount}
        categories={categories}
        onReset={handleResetFilters}
        onExport={exportCSV}
        onRefresh={loadOrders}
        onClearUpload={() => { setUploadRows(null); setUploadFilename(""); }}
        showByDate={showByDate}
        setShowByDate={setShowByDate}
      />

      {uploadRows && (
        <div className="card" style={{ marginTop: 12, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 800 }}>Preview Upload — {uploadFilename} ({uploadRows.length} rows)</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontWeight: 700 }}>Import Date</label>
              <input type="date" value={importDate} onChange={(e) => setImportDate(e.target.value)} />
              <button className="btn primary" onClick={handleImport} disabled={importing}>
                {importing ? "Importing..." : "Import Selected"}
              </button>
            </div>
          </div>

          <div style={{ overflow: "auto" }}>
            <table className="order-table upload-preview">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Skip</th>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {uploadRows.map((r, i) => (
                  <tr key={r._uid} className={`upload-row ${r._skip ? "skip" : ""}`}>
                    <td>{i + 1}</td>
                    <td><input type="checkbox" checked={!!r._skip} onChange={() => toggleSkip(r._uid)} /></td>
                    <td><input value={r.id || ""} onChange={(e) => updateUploadRow(r._uid, "id", e.target.value)} /></td>
                    <td><input value={r.customer || ""} onChange={(e) => updateUploadRow(r._uid, "customer", e.target.value)} /></td>
                    <td><input value={r.product || ""} onChange={(e) => updateUploadRow(r._uid, "product", e.target.value)} /></td>
                    <td><input value={r.category || ""} onChange={(e) => updateUploadRow(r._uid, "category", e.target.value)} /></td>
                    <td><input type="number" value={r.amount || 0} onChange={(e) => updateUploadRow(r._uid, "amount", Number(e.target.value))} /></td>
                    <td><input value={r.status || ""} onChange={(e) => updateUploadRow(r._uid, "status", e.target.value)} /></td>
                    <td><input type="date" value={r.date || importDate} onChange={(e) => updateUploadRow(r._uid, "date", e.target.value)} /></td>
                    <td><input value={r.notes || ""} onChange={(e) => updateUploadRow(r._uid, "notes", e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 12, padding: 0 }}>
        {loading ? <div style={{ padding: 16 }}>Loading…</div> : (
          showByDate
            ? <DateGroupedView orders={filtered} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
            : <OrderTable orders={filtered} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
        )}
      </div>

      {viewOrder && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3>Order Details</h3>
            <p><b>Order ID:</b> {viewOrder.id || "-"}</p>
            <p><b>Date:</b> {viewOrder.date || "-"}</p>
            <p><b>Customer:</b> {viewOrder.customer}</p>
            <p><b>Product:</b> {viewOrder.product}</p>
            <p><b>Category:</b> {viewOrder.category}</p>
            <p><b>Amount:</b> ₹{viewOrder.amount}</p>

            <div style={{ marginTop: 12 }} className="modal-actions">
              <button className="btn primary" onClick={async () => {
                try {
                  await axios.post(`${API_BASE}/orders/${viewOrder.id}/accept`);
                  void logEmployeeActivity({
                    action: "Order Accepted",
                    module: "Orders",
                    description: `Accepted order ${viewOrder.id || "-"} for ${viewOrder.customer || "customer"}.`,
                  });
                  alert("Order accepted successfully");
                  loadOrders();
                } catch (err) {
                  alert("Failed to accept order");
                }
              }}>✅ Accept</button>
              <button className="btn danger" onClick={async () => {
                try {
                  await axios.post(`${API_BASE}/orders/${viewOrder.id}/reject`);
                  void logEmployeeActivity({
                    action: "Order Rejected",
                    module: "Orders",
                    description: `Rejected order ${viewOrder.id || "-"} for ${viewOrder.customer || "customer"}.`,
                  });
                  alert("Order rejected successfully");
                  loadOrders();
                } catch (err) {
                  alert("Failed to reject order");
                }
              }}>❌ Reject</button>
              <button className="btn outline" onClick={closeView}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editOrder && (
        <OrderEditModal order={editOrder} onClose={() => setEditOrder(null)} onUpdate={handleUpdateOrder} />
      )}
    </div>
  );
}

/* ---------- OrderEditModal (unchanged) ---------- */
function OrderEditModal({ order, onClose, onUpdate }) {
  const [form, setForm] = useState(order || {});
  useEffect(() => setForm(order || {}), [order]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = (e) => { e.preventDefault(); onUpdate && onUpdate(form); onClose && onClose(); };

  if (!order) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h3>Edit Order</h3>
        <form onSubmit={submit} className="edit-form">
          <input name="id" value={form.id || ""} onChange={handleChange} placeholder="Order ID" />
          <input name="customer" value={form.customer || ""} onChange={handleChange} placeholder="Customer" />
          <input name="product" value={form.product || ""} onChange={handleChange} placeholder="Product" />
          <input name="category" value={form.category || ""} onChange={handleChange} placeholder="Category" />
          <input name="amount" value={form.amount || ""} onChange={handleChange} placeholder="Amount" />
          <select name="status" value={form.status || "Pending"} onChange={handleChange}>
            <option>Pending</option>
            <option>Accepted</option>
            <option>Rejected</option>
            <option>Cancelled</option>
          </select>

          <div className="modal-actions" style={{ justifyContent: "flex-end" }}>
            <button type="submit" className="btn primary">Save</button>
            <button type="button" className="btn outline" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
